import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Menu, X } from 'lucide-react';
import MovoFleetLogo from '@/shared/ui/branding/MovoFleetLogo';
import { primaryButtonClass, secondaryButtonClass } from '@/shared/ui/formClasses';
import { cn } from '@/shared/lib/utils';
import { useSession } from '@/features/auth';

const FOCUS_RING = 'rounded-sm focus-visible:focus-ring';
const NAV_LINK = `block text-subtle transition-colors duration-150 hover:text-strong ${FOCUS_RING}`;

const menuItems = [
  { name: 'Moduli', href: '#moduli' },
  { name: 'Prezzi', href: '#prezzi' },
  { name: 'Demo', href: '/richiedi-demo' },
];

function MenuLinks({ className }) {
  return (
    <ul className={className}>
      {menuItems.map((item) => (
        <li key={item.name}>
          {item.href.startsWith('/') ? (
            <Link to={item.href} className={NAV_LINK}>
              {item.name}
            </Link>
          ) : (
            <a href={item.href} className={NAV_LINK}>
              {item.name}
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}

/** La barra fissa della landing: si compatta dopo lo scroll, su mobile apre un menu. */
export function LandingHeader() {
  const { user } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header>
      <nav
        aria-label="Principale"
        data-state={menuOpen ? 'active' : undefined}
        className="group fixed z-20 w-full px-2"
      >
        <div
          className={cn(
            'mx-auto mt-2 px-6 transition-all duration-300',
            isScrolled
              ? 'max-w-lg rounded-full border border-default bg-default/80 py-0.5 shadow-resting backdrop-blur-lg lg:px-4'
              : 'max-w-7xl py-0 lg:px-12'
          )}
        >
          <div
            className={cn(
              'relative flex flex-wrap items-center justify-between gap-6 lg:gap-0',
              isScrolled ? 'py-1.5' : 'py-3 lg:py-4'
            )}
          >
            <div className="flex w-full justify-between md:w-auto">
              <Link
                to="/"
                aria-label="MovoFleet, pagina iniziale"
                className={`flex items-center ${FOCUS_RING}`}
              >
                <MovoFleetLogo
                  variant="marketing"
                  className={cn(
                    'origin-left transition-transform duration-300',
                    isScrolled && 'scale-[0.78]'
                  )}
                />
              </Link>
              <button
                type="button"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label={menuOpen ? 'Chiudi menu' : 'Apri menu'}
                aria-expanded={menuOpen}
                aria-controls="landing-menu"
                className={`relative z-20 -m-2.5 -mr-4 block cursor-pointer p-2.5 md:hidden ${FOCUS_RING}`}
              >
                {menuOpen ? (
                  <X aria-hidden className="size-5" />
                ) : (
                  <Menu aria-hidden className="size-5" />
                )}
              </button>
            </div>

            <div className="absolute inset-0 m-auto hidden size-fit md:block">
              <MenuLinks className={cn('flex text-sm', isScrolled ? 'gap-5' : 'gap-8')} />
            </div>

            <div
              id="landing-menu"
              className="surface-overlay mb-6 hidden w-full flex-wrap items-center justify-end space-y-8 rounded-3xl p-6 group-data-[state=active]:block md:m-0 md:flex md:w-fit md:flex-nowrap md:gap-6 md:space-y-0 md:border-transparent md:bg-transparent md:p-0 md:shadow-none md:group-data-[state=active]:flex"
            >
              <MenuLinks className="space-y-6 text-base md:hidden" />
              <div className="flex w-full flex-col gap-3 sm:flex-row md:w-fit">
                <Link
                  to={user ? '/dashboard' : '/login'}
                  className={cn(secondaryButtonClass, isScrolled && 'md:hidden')}
                >
                  {user ? "Entra nell'app" : 'Accedi'}
                </Link>
                <Link to="/richiedi-demo" className={primaryButtonClass}>
                  Richiedi demo
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}
